import type { ApiFilestackImageWithTextData } from '#/api/types/ApiFilestackImageData';
import { FilestackImage } from '#/components/FilestackImage/FilestackImage';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { H3 } from '#/components/Text/H3';
import { RichText } from '#/components/Text/RichText';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import { CmsThreeStepCtaBlockStepBox } from './CmsThreeStepCtaBlockStepBox';
import {
  CmsThreeStepCtaBlockHeadlineStyledRichText,
  CmsThreeStepCtaBlockStepHolder,
  CmsTreeStepCtaBlockStep_Box_1,
  CmsTreeStepCtaBlockStep_Box_2,
} from './CmsTreeStepCtaBlockStep.styled';

type Props = {
  blockIndex: number;
  headline: string;
  image: ApiFilestackImageWithTextData | null;
  text: string;
  url: string | null;
  withImages: boolean;
  withLinks: boolean;
  large: boolean;
  pageUpdated: string;
  step: string;
  qaId: string;
};

export function CmsTreeStepCtaBlockStep({
  blockIndex,
  headline,
  image,
  text,
  url,
  withImages,
  withLinks,
  large,
  pageUpdated,
  step,
  qaId,
}: Props) {
  const headlineNode = (
    <CmsThreeStepCtaBlockHeadlineStyledRichText html={headline} />
  );

  return (
    <CmsThreeStepCtaBlockStepHolder
      onClick={() => {
        if (!url || !window) return;

        trackClicked('CMS Block', {
          block_position: blockIndex,
          block_type: 'Three Step Call to Action',
          cta_type: 'Tile',
          page_updated: pageUpdated,
          step,
        });

        window.open(url, '_self');
      }}
      data-qa-id={qaId}
      $large={large}
      $withLinks={withLinks}
      style={{
        maxWidth: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexDirection: 'column',
        flex: '0 0 auto',
      }}
    >
      <CmsThreeStepCtaBlockStepBox
        withLinks={withLinks}
        large={large}
        hasUrl={Boolean(url)}
      >
        {(Boolean(image) || withImages) && (
          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
              marginBottom: 24,
              width: '100%',
              height: large ? 0 : 80,
              paddingBottom: large ? `${200 / 3}%` : 0,
            }}
          >
            {image && (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                }}
              >
                <FilestackImage
                  qaId={`cta-step-image${large ? '-large' : ''}`}
                  alt={image.altText ?? ''}
                  data={image}
                  height={large ? 240 : 80}
                  width={352}
                  noCrop
                  expandSize
                  objectFit={large ? 'cover' : 'contain'}
                />
              </div>
            )}
          </div>
        )}
        <div style={{ width: '100%' }}>
          <CmsTreeStepCtaBlockStep_Box_2
            data-not-large-or-with-links={
              !(large || withLinks) ? true : undefined
            }
          >
            <H3 styleAs={large ? 'h4' : 'h3'} style={{ marginBottom: '8px' }}>
              {url ? (
                <StyledNativeLinkWithRef
                  href={url}
                  onClick={(event) =>
                    trackAnchorClicked(event, 'CMS Block', {
                      block_position: blockIndex,
                      block_type: 'Three Step Call to Action',
                      cta_type: 'Tile',
                      page_updated: pageUpdated,
                      step,
                    })
                  }
                >
                  {headlineNode}
                </StyledNativeLinkWithRef>
              ) : (
                headlineNode
              )}
            </H3>
          </CmsTreeStepCtaBlockStep_Box_2>
          <CmsTreeStepCtaBlockStep_Box_1
            data-not-large-or-with-links={
              !(large || withLinks) ? true : undefined
            }
          >
            <RichText
              html={text}
              $small
              analyticsData={{
                eventName: 'CMS Block',
                eventProperties: {
                  block_position: blockIndex,
                  block_type: 'Three Step CTA',
                },
              }}
            />
          </CmsTreeStepCtaBlockStep_Box_1>
        </div>
      </CmsThreeStepCtaBlockStepBox>
    </CmsThreeStepCtaBlockStepHolder>
  );
}
